<!-- src/lib/components/GitPanel.svelte -->
<!--
  Embedded git inspector — three tabs (Status, History, Diff) that sit above
  a scrollable body panel. Reads from /api/v1/git/{status,log,diff}.

  rootPath is the repo (or any path inside a repo — libgit2 discovers).
  Everything is read-only for now: no stage/commit/push from the UI.
  That's intentional — the terminal pane is already right there if you
  want to do real git operations.
-->
<script lang="ts">
  import { api, apiFetch } from '../api.ts';
  import { timeAgo } from '../format-time.ts';

  interface Props {
    rootPath?: string | null;
  }
  let { rootPath = null }: Props = $props();

  interface StatusEntry {
    path:     string;
    status:   'modified' | 'added' | 'deleted' | 'renamed' | 'untracked' | 'conflicted' | 'unknown';
    staged:   boolean;
    unstaged: boolean;
    old_path: string | null;
  }

  interface StatusResponse {
    branch:   string | null;
    ahead:    number;
    behind:   number;
    head_sha: string | null;
    entries:  StatusEntry[];
    clean:    boolean;
  }

  interface GitCommit {
    sha:       string;
    short_sha: string;
    author:    string;
    email:     string;
    timestamp: number;
    message:   string;
    summary:   string;
    parents:   string[];
  }

  interface GitRemote {
    host:    string;
    owner:   string;
    repo:    string;
    raw_url: string;
    web_url: string;
  }

  interface GitHubUser {
    login:      string;
    avatar_url: string;
  }
  interface GitHubPull {
    number:     number;
    title:      string;
    state:      string;
    html_url:   string;
    user:       GitHubUser | null;
    created_at: string;
    updated_at: string;
    draft:      boolean;
    head:       { ref: string } | null;
    base:       { ref: string } | null;
  }
  interface GitHubIssue {
    number:       number;
    title:        string;
    state:        string;
    html_url:     string;
    user:         GitHubUser | null;
    created_at:   string;
    updated_at:   string;
    pull_request?: unknown; // GitHub returns PRs on the /issues endpoint too
    comments:     number;
  }

  type Tab = 'status' | 'history' | 'diff' | 'github';
  let activeTab = $state<Tab>('status');

  let statusData = $state<StatusResponse | null>(null);
  let commits    = $state<GitCommit[]>([]);
  let diffText   = $state<string>('');
  let selectedSha = $state<string | null>(null);
  let loading    = $state(false);
  let errorMsg   = $state<string | null>(null);

  // GitHub integration state
  let remoteInfo   = $state<GitRemote | null>(null);
  let ghPulls      = $state<GitHubPull[]>([]);
  let ghIssues     = $state<GitHubIssue[]>([]);
  let ghLoading    = $state(false);
  let ghError      = $state<string | null>(null);
  let ghNeedsToken = $state(false); // true after a 404 on a probably-private repo
  let patInput     = $state('');
  let savingPat    = $state(false);

  async function loadStatus() {
    if (!rootPath) return;
    loading = true;
    errorMsg = null;
    try {
      statusData = await api<StatusResponse>(`/api/v1/git/status?path=${encodeURIComponent(rootPath)}`);
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to read git status';
      statusData = null;
    } finally {
      loading = false;
    }
  }

  async function loadHistory() {
    if (!rootPath) return;
    loading = true;
    errorMsg = null;
    try {
      commits = await api<GitCommit[]>(`/api/v1/git/log?path=${encodeURIComponent(rootPath)}&limit=80`);
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to read git log';
      commits = [];
    } finally {
      loading = false;
    }
  }

  // ── GitHub integration ──────────────────────────────────────────────────
  //
  // Flow: on rootPath change we probe /api/v1/git/remote. If origin is a
  // GitHub remote, we stash it in remoteInfo. When the user opens the
  // GitHub tab we fetch PRs + issues in parallel via the server proxy
  // (anonymous works up to 60 req/hr; a stored PAT unlocks 5000/hr +
  // private repos).
  //
  // ghNeedsToken is set when the proxy returns 404/401 on a repo that has
  // a valid-looking remote — the most likely cause is a private repo
  // without a token. We render an inline paste form in that case.

  async function loadRemote() {
    if (!rootPath) { remoteInfo = null; return; }
    try {
      remoteInfo = await api<GitRemote>(`/api/v1/git/remote?path=${encodeURIComponent(rootPath)}`);
    } catch {
      // 404 just means "no github remote" — not an error to show the user.
      remoteInfo = null;
    }
  }

  async function loadGitHubData() {
    if (!remoteInfo) return;
    const { owner, repo } = remoteInfo;
    ghLoading = true;
    ghError   = null;
    ghNeedsToken = false;
    try {
      // workspace_id is optional — server uses it to inject a stored PAT.
      // We pass 'default' for now; once multi-workspace lands this should
      // come from a store.
      const qs = 'workspace_id=default&state=open';
      const [pullsRes, issuesRes] = await Promise.all([
        apiFetch(`/api/v1/github/repos/${owner}/${repo}/pulls?${qs}`),
        apiFetch(`/api/v1/github/repos/${owner}/${repo}/issues?${qs}`),
      ]);
      if (pullsRes.status === 404 || pullsRes.status === 401) {
        ghNeedsToken = true;
        ghError = 'Repo not found or private — paste a GitHub PAT below to unlock.';
        ghPulls  = [];
        ghIssues = [];
        return;
      }
      if (!pullsRes.ok) throw new Error(`PRs: HTTP ${pullsRes.status}`);
      if (!issuesRes.ok) throw new Error(`Issues: HTTP ${issuesRes.status}`);
      const pullsJson  = (await pullsRes.json()) as GitHubPull[];
      const issuesJson = (await issuesRes.json()) as GitHubIssue[];
      ghPulls  = pullsJson;
      // The /issues endpoint mixes issues + PRs; filter PRs out client-side.
      ghIssues = issuesJson.filter((i) => !i.pull_request);
    } catch (e: unknown) {
      ghError = e instanceof Error ? e.message : 'Failed to load GitHub data';
    } finally {
      ghLoading = false;
    }
  }

  async function savePat() {
    const token = patInput.trim();
    if (!token) return;
    savingPat = true;
    try {
      const res = await apiFetch('/api/v1/integrations/github/token?workspace_id=default', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ access_token: token }),
      });
      if (!res.ok) throw new Error(`Save token failed: HTTP ${res.status}`);
      patInput = '';
      ghNeedsToken = false;
      await loadGitHubData();
    } catch (e: unknown) {
      ghError = e instanceof Error ? e.message : 'Failed to save token';
    } finally {
      savingPat = false;
    }
  }

  async function loadDiff(sha?: string) {
    if (!rootPath) return;
    loading = true;
    errorMsg = null;
    try {
      // IMPORTANT: api<T>() does .json() on the response, which fails for the
      // text/plain diff endpoint. Use raw fetch here.
      const path = sha
        ? `/api/v1/git/diff?path=${encodeURIComponent(rootPath)}&sha=${sha}`
        : `/api/v1/git/diff?path=${encodeURIComponent(rootPath)}`;
      const res = await apiFetch(path);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      diffText = await res.text();
      selectedSha = sha ?? null;
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to read diff';
      diffText = '';
    } finally {
      loading = false;
    }
  }

  function switchTab(t: Tab) {
    activeTab = t;
    if (t === 'status')  void loadStatus();
    if (t === 'history') void loadHistory();
    if (t === 'diff' && !diffText) void loadDiff(); // default: working-tree diff
    if (t === 'github' && remoteInfo && ghPulls.length === 0 && ghIssues.length === 0 && !ghError) {
      void loadGitHubData();
    }
  }

  // When the root changes, reload whatever tab is active AND re-probe the
  // remote so the GitHub tab reflects the new repo.
  $effect(() => {
    if (!rootPath) return;
    void loadRemote();
    if (activeTab === 'status')  void loadStatus();
    if (activeTab === 'history') void loadHistory();
    if (activeTab === 'diff')    void loadDiff(selectedSha ?? undefined);
    if (activeTab === 'github') {
      // loadGitHubData runs once remoteInfo is populated — handled by
      // its own effect below.
    }
  });

  // When remoteInfo lands for the first time (or changes), auto-fetch
  // GitHub data if the user is on the github tab.
  $effect(() => {
    if (activeTab === 'github' && remoteInfo) {
      void loadGitHubData();
    }
  });

  function openCommitDiff(sha: string) {
    activeTab = 'diff';
    void loadDiff(sha);
  }

  function statusColor(s: StatusEntry['status']): string {
    switch (s) {
      case 'modified':   return 'oklch(0.78 0.16 85)';     // gold
      case 'added':      return 'oklch(0.76 0.20 150 / 0.85)'; // keep greenish for "new"
      case 'deleted':    return 'oklch(0.68 0.24 25)';     // red
      case 'renamed':    return 'oklch(0.68 0.18 265)';    // blue
      case 'untracked':  return 'oklch(0.55 0.03 275 / 0.7)'; // muted
      case 'conflicted': return 'oklch(0.75 0.26 25)';     // bright red
      default:           return 'oklch(0.55 0.03 275 / 0.5)';
    }
  }

  function statusGlyph(s: StatusEntry['status']): string {
    switch (s) {
      case 'modified':   return 'M';
      case 'added':      return 'A';
      case 'deleted':    return 'D';
      case 'renamed':    return 'R';
      case 'untracked':  return '?';
      case 'conflicted': return '!';
      default:           return '·';
    }
  }

  // timeAgo accepts epoch-seconds, so git commit timestamps Just Work.
  function formatTimestamp(ts: number): string { return timeAgo(ts); }

  // Parse the diff into renderable blocks so we can color each line
  interface DiffLine {
    kind: 'file' | 'hunk' | 'add' | 'del' | 'ctx' | 'meta';
    text: string;
  }
  const diffLines: DiffLine[] = $derived((() => {
    if (!diffText) return [];
    return diffText.split('\n').map((raw): DiffLine => {
      if (raw.startsWith('diff --git') || raw.startsWith('index ') ||
          raw.startsWith('--- ') || raw.startsWith('+++ ') ||
          raw.startsWith('new file') || raw.startsWith('deleted file') ||
          raw.startsWith('rename ') || raw.startsWith('similarity ')) {
        return { kind: 'file', text: raw };
      }
      if (raw.startsWith('@@')) return { kind: 'hunk', text: raw };
      if (raw.startsWith('+'))  return { kind: 'add',  text: raw };
      if (raw.startsWith('-'))  return { kind: 'del',  text: raw };
      return { kind: 'ctx', text: raw };
    });
  })());
</script>

<div class="git-panel">
  <div class="git-header">
    <span class="header-label">Git</span>
    {#if statusData?.branch}
      <span class="branch-tag" title={statusData.head_sha ?? ''}>
        {statusData.branch}
        {#if statusData.ahead > 0 || statusData.behind > 0}
          <span class="ahead-behind">
            {#if statusData.ahead > 0}<span class="ahead">↑{statusData.ahead}</span>{/if}
            {#if statusData.behind > 0}<span class="behind">↓{statusData.behind}</span>{/if}
          </span>
        {/if}
      </span>
    {/if}
  </div>

  <nav class="git-tabs">
    <button
      class="git-tab"
      class:active={activeTab === 'status'}
      onclick={() => switchTab('status')}
    >Status</button>
    <button
      class="git-tab"
      class:active={activeTab === 'history'}
      onclick={() => switchTab('history')}
    >History</button>
    <button
      class="git-tab"
      class:active={activeTab === 'diff'}
      onclick={() => switchTab('diff')}
    >Diff</button>
    <button
      class="git-tab"
      class:active={activeTab === 'github'}
      onclick={() => switchTab('github')}
      title={remoteInfo ? `${remoteInfo.owner}/${remoteInfo.repo}` : 'No GitHub remote'}
    >GitHub{#if remoteInfo}<span class="tab-dot"></span>{/if}</button>
  </nav>

  <div class="git-body">
    {#if !rootPath}
      <div class="empty-state">
        <p>No folder open.</p>
        <p class="empty-hint">Open a folder in the Files tab to see its git state.</p>
      </div>
    {:else if loading}
      <div class="loading">Loading…</div>
    {:else if errorMsg}
      <div class="error-state">
        <p>{errorMsg}</p>
      </div>
    {:else if activeTab === 'status'}
      {#if statusData?.clean}
        <div class="empty-state">
          <p class="clean-title">Working tree clean</p>
          <p class="empty-hint">Nothing to commit.</p>
        </div>
      {:else if statusData?.entries}
        <ul class="status-list">
          {#each statusData.entries as entry (entry.path)}
            <li class="status-row">
              <span class="status-glyph" style="color: {statusColor(entry.status)}">{statusGlyph(entry.status)}</span>
              <span class="status-path" title={entry.path}>{entry.path}</span>
              {#if entry.staged}<span class="stage-badge staged">●</span>{/if}
            </li>
          {/each}
        </ul>
      {/if}
    {:else if activeTab === 'history'}
      {#if commits.length === 0}
        <div class="empty-state"><p>No commits.</p></div>
      {:else}
        <ul class="commit-list">
          {#each commits as c (c.sha)}
            <li>
              <button
                type="button"
                class="commit-row"
                class:selected={selectedSha === c.sha}
                onclick={() => openCommitDiff(c.sha)}
                title={c.message}
              >
                <span class="commit-sha">{c.short_sha}</span>
                <div class="commit-info">
                  <div class="commit-summary">{c.summary}</div>
                  <div class="commit-meta">
                    <span class="commit-author">{c.author}</span>
                    <span class="commit-time">{formatTimestamp(c.timestamp)}</span>
                  </div>
                </div>
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    {:else if activeTab === 'diff'}
      {#if !diffText}
        <div class="empty-state">
          <p>No diff.</p>
          <p class="empty-hint">Pick a commit from History or modify a file in the working tree.</p>
        </div>
      {:else}
        <pre class="diff-view">{#each diffLines as line}<span class="diff-line diff-{line.kind}">{line.text}
</span>{/each}</pre>
      {/if}
    {:else if activeTab === 'github'}
      {#if !remoteInfo}
        <div class="empty-state">
          <p>No GitHub remote.</p>
          <p class="empty-hint">This repo's <code>origin</code> doesn't point at github.com.</p>
        </div>
      {:else}
        <div class="gh-repo-badge">
          <div class="gh-repo-top">
            <span class="gh-repo-owner">{remoteInfo.owner}</span>
            <span class="gh-repo-slash">/</span>
            <span class="gh-repo-name">{remoteInfo.repo}</span>
          </div>
          <a class="gh-repo-link" href={remoteInfo.web_url} target="_blank" rel="noopener noreferrer">
            {remoteInfo.host}
          </a>
        </div>

        {#if ghLoading}
          <div class="loading">Loading GitHub…</div>
        {:else if ghNeedsToken}
          <div class="gh-pat-form">
            <p class="gh-pat-title">Paste a GitHub Personal Access Token</p>
            <p class="gh-pat-hint">
              Needs <code>repo</code> scope. Stored locally in the server DB.
              Create one at <a href="https://github.com/settings/tokens/new?scopes=repo&description=agentboard" target="_blank" rel="noopener noreferrer">github.com/settings/tokens</a>.
            </p>
            <input
              class="gh-pat-input"
              type="password"
              placeholder="ghp_…"
              bind:value={patInput}
              disabled={savingPat}
              onkeydown={(e) => { if (e.key === 'Enter') void savePat(); }}
            />
            <button class="gh-pat-save" onclick={() => void savePat()} disabled={savingPat || !patInput.trim()}>
              {savingPat ? 'Saving…' : 'Save token'}
            </button>
            {#if ghError}<p class="gh-error">{ghError}</p>{/if}
          </div>
        {:else if ghError}
          <div class="error-state"><p>{ghError}</p></div>
        {:else}
          <section class="gh-section">
            <h3 class="gh-section-title">Open pull requests <span class="gh-count">{ghPulls.length}</span></h3>
            {#if ghPulls.length === 0}
              <p class="gh-empty">No open PRs.</p>
            {:else}
              <ul class="gh-list">
                {#each ghPulls as pr (pr.number)}
                  <li>
                    <a class="gh-item" href={pr.html_url} target="_blank" rel="noopener noreferrer">
                      <span class="gh-num">#{pr.number}</span>
                      <span class="gh-title">{pr.title}</span>
                      {#if pr.draft}<span class="gh-badge gh-draft">draft</span>{/if}
                      {#if pr.user}<span class="gh-author">{pr.user.login}</span>{/if}
                    </a>
                  </li>
                {/each}
              </ul>
            {/if}
          </section>

          <section class="gh-section">
            <h3 class="gh-section-title">Open issues <span class="gh-count">{ghIssues.length}</span></h3>
            {#if ghIssues.length === 0}
              <p class="gh-empty">No open issues.</p>
            {:else}
              <ul class="gh-list">
                {#each ghIssues as iss (iss.number)}
                  <li>
                    <a class="gh-item" href={iss.html_url} target="_blank" rel="noopener noreferrer">
                      <span class="gh-num">#{iss.number}</span>
                      <span class="gh-title">{iss.title}</span>
                      {#if iss.comments > 0}<span class="gh-comments">{iss.comments}</span>{/if}
                      {#if iss.user}<span class="gh-author">{iss.user.login}</span>{/if}
                    </a>
                  </li>
                {/each}
              </ul>
            {/if}
          </section>
        {/if}
      {/if}
    {/if}
  </div>
</div>

<style>
  .git-panel {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    color: oklch(0.80 0.05 310 / 0.85);
    font-size: 0.78rem;
    overflow: hidden;
  }

  .git-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.55rem 0.75rem 0.45rem 0.75rem;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.12);
  }

  .header-label {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: oklch(0.72 0.08 310 / 0.6);
  }

  .branch-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.68rem;
    color: oklch(0.86 0.18 310 / 0.85);
    background: oklch(0.72 0.28 310 / 0.10);
    border: 1px solid oklch(0.72 0.28 310 / 0.28);
    border-radius: 3px;
    padding: 0.1rem 0.4rem;
  }
  .ahead-behind { display: inline-flex; gap: 0.25rem; }
  .ahead  { color: oklch(0.78 0.16 85); }
  .behind { color: oklch(0.68 0.24 25); }

  .git-tabs {
    flex-shrink: 0;
    display: flex;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.12);
  }
  .git-tab {
    flex: 1;
    background: transparent;
    border: none;
    color: oklch(0.55 0.05 310 / 0.6);
    padding: 0.45rem 0.5rem;
    cursor: pointer;
    font-size: 0.7rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    transition: color 120ms ease, background 120ms ease, border-color 120ms ease;
    border-bottom: 1px solid transparent;
  }
  .git-tab:hover {
    color: oklch(0.85 0.10 310);
    background: oklch(0.72 0.28 310 / 0.06);
  }
  .git-tab.active {
    color: oklch(0.92 0.12 310);
    border-bottom-color: oklch(0.78 0.26 310 / 0.70);
    background: oklch(0.22 0.10 310 / 0.25);
  }

  .git-body {
    flex: 1;
    overflow-y: auto;
    padding: 0.35rem 0;
  }
  .git-body::-webkit-scrollbar { width: 8px; }
  .git-body::-webkit-scrollbar-thumb {
    background: oklch(0.30 0.05 275 / 0.4);
    border-radius: 4px;
  }
  .git-body::-webkit-scrollbar-thumb:hover {
    background: oklch(0.40 0.10 310 / 0.5);
  }

  .status-list,
  .commit-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .status-row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.3rem 0.75rem;
    transition: background 80ms ease;
    cursor: default;
  }
  .status-row:hover {
    background: oklch(0.72 0.28 310 / 0.06);
  }
  .status-glyph {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    font-size: 0.75rem;
    width: 0.9rem;
    text-align: center;
    flex-shrink: 0;
  }
  .status-path {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.72rem;
    color: oklch(0.80 0.04 310 / 0.85);
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .stage-badge {
    font-size: 0.5rem;
    color: oklch(0.78 0.26 310);
  }

  .commit-row {
    /* reset <button> browser defaults */
    background: transparent;
    border: none;
    color: inherit;
    font: inherit;
    width: 100%;
    text-align: left;

    display: flex;
    gap: 0.6rem;
    padding: 0.45rem 0.75rem;
    cursor: pointer;
    transition: background 80ms ease;
  }
  .commit-row:hover {
    background: oklch(0.72 0.28 310 / 0.06);
  }
  .commit-row.selected {
    background: oklch(0.72 0.28 310 / 0.12);
  }
  .commit-sha {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.68rem;
    color: oklch(0.78 0.16 85 / 0.85); /* gold sha */
    flex-shrink: 0;
    width: 3.5rem;
  }
  .commit-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }
  .commit-summary {
    font-size: 0.76rem;
    color: oklch(0.90 0.04 310);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .commit-row.selected .commit-summary {
    color: oklch(0.96 0.08 310);
  }
  .commit-meta {
    display: flex;
    gap: 0.55rem;
    font-size: 0.65rem;
    color: oklch(0.55 0.05 310 / 0.60);
  }
  .commit-author {
    max-width: 60%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .diff-view {
    margin: 0;
    padding: 0.4rem 0.5rem 1rem 0.75rem;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.7rem;
    line-height: 1.5;
    white-space: pre;
    color: oklch(0.78 0.03 310 / 0.85);
    tab-size: 4;
  }
  .diff-line {
    display: block;
  }
  .diff-file {
    color: oklch(0.65 0.08 310 / 0.85);
    font-weight: 600;
  }
  .diff-hunk {
    color: oklch(0.78 0.22 310 / 0.85);
    background: oklch(0.72 0.28 310 / 0.05);
    padding: 0 0.1rem;
  }
  .diff-add {
    color: oklch(0.82 0.14 85);
    background: oklch(0.78 0.16 85 / 0.06);
  }
  .diff-del {
    color: oklch(0.72 0.22 25);
    background: oklch(0.68 0.24 25 / 0.06);
  }
  .diff-ctx {
    color: oklch(0.58 0.03 310 / 0.80);
  }

  .empty-state,
  .loading,
  .error-state {
    padding: 1.5rem 1rem;
    text-align: center;
    color: oklch(0.55 0.05 310 / 0.55);
    font-size: 0.78rem;
  }
  .empty-hint {
    margin: 0.35rem 0 0 0;
    font-size: 0.72rem;
    color: oklch(0.55 0.05 310 / 0.5);
  }
  .clean-title {
    color: oklch(0.85 0.12 310);
    margin: 0 0 0.35rem 0;
    font-size: 0.85rem;
    font-weight: 500;
  }
  .error-state p {
    color: oklch(0.72 0.22 25 / 0.85);
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.72rem;
    margin: 0;
    word-break: break-word;
  }

  /* ── GitHub tab ────────────────────────────────────────────────────── */

  .tab-dot {
    display: inline-block;
    width: 5px;
    height: 5px;
    margin-left: 0.35rem;
    border-radius: 50%;
    background: oklch(0.80 0.20 150);
    vertical-align: middle;
  }

  .gh-repo-badge {
    margin: 0 0.75rem 0.5rem 0.75rem;
    padding: 0.5rem 0.65rem;
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 4px;
    background: oklch(0.17 0.022 275);
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }
  .gh-repo-top {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.85rem;
    color: oklch(0.92 0.06 310);
  }
  .gh-repo-owner  { color: oklch(0.78 0.08 310 / 0.75); }
  .gh-repo-slash  { color: oklch(0.55 0.05 310 / 0.5); margin: 0 0.15rem; }
  .gh-repo-name   { font-weight: 600; }
  .gh-repo-link {
    font-size: 0.65rem;
    color: oklch(0.68 0.12 265 / 0.85);
    text-decoration: none;
  }
  .gh-repo-link:hover { text-decoration: underline; }

  .gh-section {
    padding: 0.25rem 0.75rem 0.6rem 0.75rem;
  }
  .gh-section-title {
    margin: 0.6rem 0 0.35rem 0;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: oklch(0.72 0.08 310 / 0.6);
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
  .gh-count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.62rem;
    color: oklch(0.80 0.05 310 / 0.5);
    background: oklch(0.22 0.05 275 / 0.4);
    padding: 0.05rem 0.3rem;
    border-radius: 8px;
  }
  .gh-empty {
    margin: 0.25rem 0 0 0;
    font-size: 0.72rem;
    color: oklch(0.55 0.05 310 / 0.5);
    font-style: italic;
  }
  .gh-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .gh-item {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.35rem 0.4rem;
    margin: 0 -0.4rem;
    border-radius: 3px;
    text-decoration: none;
    color: oklch(0.88 0.04 310);
    font-size: 0.74rem;
    transition: background 80ms ease;
  }
  .gh-item:hover {
    background: oklch(0.72 0.28 310 / 0.08);
  }
  .gh-num {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.66rem;
    color: oklch(0.78 0.16 85 / 0.75);
    flex-shrink: 0;
    width: 2.5rem;
  }
  .gh-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .gh-author {
    font-size: 0.65rem;
    color: oklch(0.55 0.05 310 / 0.6);
    flex-shrink: 0;
  }
  .gh-comments {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.62rem;
    color: oklch(0.68 0.12 265 / 0.75);
    flex-shrink: 0;
  }
  .gh-badge {
    font-size: 0.6rem;
    padding: 0.05rem 0.35rem;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    flex-shrink: 0;
  }
  .gh-draft {
    background: oklch(0.30 0.03 275 / 0.6);
    color: oklch(0.72 0.05 310 / 0.8);
    border: 1px solid oklch(0.40 0.05 275 / 0.5);
  }

  .gh-pat-form {
    margin: 0.35rem 0.75rem;
    padding: 0.75rem;
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 4px;
    background: oklch(0.13 0.02 275);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .gh-pat-title {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
    color: oklch(0.92 0.06 310);
  }
  .gh-pat-hint {
    margin: 0;
    font-size: 0.68rem;
    color: oklch(0.55 0.05 310 / 0.65);
    line-height: 1.5;
  }
  .gh-pat-hint a {
    color: oklch(0.68 0.12 265);
    text-decoration: none;
  }
  .gh-pat-hint a:hover { text-decoration: underline; }
  .gh-pat-input {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.72rem;
    padding: 0.4rem 0.55rem;
    background: oklch(0.065 0.015 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    border-radius: 3px;
    color: oklch(0.92 0.04 310);
    outline: none;
  }
  .gh-pat-input:focus {
    border-color: oklch(0.72 0.26 310 / 0.55);
  }
  .gh-pat-save {
    align-self: flex-start;
    font-size: 0.7rem;
    font-weight: 500;
    padding: 0.4rem 0.8rem;
    background: oklch(0.30 0.18 310 / 0.45);
    color: oklch(0.96 0.06 310);
    border: 1px solid oklch(0.72 0.26 310 / 0.40);
    border-radius: 3px;
    cursor: pointer;
    transition: background 100ms ease;
  }
  .gh-pat-save:hover:not(:disabled) {
    background: oklch(0.38 0.22 310 / 0.55);
  }
  .gh-pat-save:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .gh-error {
    margin: 0;
    font-size: 0.7rem;
    color: oklch(0.72 0.22 25 / 0.85);
    font-family: 'JetBrains Mono', monospace;
  }

  code {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.92em;
    padding: 0.05rem 0.3rem;
    background: oklch(0.22 0.03 275 / 0.5);
    border-radius: 2px;
  }
</style>
